import { createPortal } from 'react-dom';

// core
import umlConnectorEngine from '../../../Core/UmlConnectorEngine.js';
import umlCanvasEngine from '../../../Core/UmlCanvasEngine.js';

// controls
import { JxFormResizableBase, JxTitleBar } from '@/JxControls/index';

// helpers
import { labelForShape } from '../UmlWorldMapHelpers.jsx';

// locals
import './UmlHiddenItemsDlg.css';
import { labelForConnector, buildShapeRows } from './UmlHiddenItemsHelpers.js';
import { TITLE_BAR_ACTIONS } from './UmlHiddenItemsDlgTitleBarActions.jsx';
import UmlHiddenItemsRow from './UmlHiddenItemsRow.jsx';

/**
 * UmlHiddenItemsDlg — lists every id in umlCanvasEngine.viewConfig.userHiddenItems
 */
export default class UmlHiddenItemsDlg extends JxFormResizableBase {
    state = {
        ...this.state,
    };

    constructor(props) {
        super(props);
        this.name = 'UmlHiddenItemsDlg';
    }

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribeCanvas = umlCanvasEngine.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount() {
        this._unsubscribeCanvas?.();
        super.componentWillUnmount();
    }

    getDefaultGeo() {
        return { top: 160, left: 790, width: 340, height: 320 };
    }

    handleClose = () => {
        this.props.onClose?.();
    };

    handleUnhideAll = () => {
        umlCanvasEngine.clearHiddenItems();
    };

    handleShowItem = (itemId) => {
        umlCanvasEngine.setItemHidden(itemId, false);
    };

    /**
     * Resolves each hidden id to a shape (expanded into a full descendant tree
     * via buildShapeRows, since a hidden package cascades to everything nested
     * inside it — see resolveUserHiddenItems.js) or a connector (always a
     * single top-level row, connectors have no children). An id can go stale if
     * the item was deleted while still hidden (removeShape/removeConnector
     * clean this list up on delete, see UmlCanvasEngine.removeFromHiddenItems,
     * but older data or edge cases could still leave one) — stale ids are
     * silently skipped rather than rendered as broken rows.
     * @returns {{id:string, type:'shape'|'connector', kind:string, label:string, depth:number, isTopLevel:boolean}[]}
     */
    getItems() {
        const hiddenIds = umlCanvasEngine.viewConfig.userHiddenItems || [];
        return hiddenIds.flatMap(function resolveItem(id) {
            const shape = umlCanvasEngine.getShape(id);
            if (shape) {
                return buildShapeRows(shape, 0, labelForShape);
            }
            const connector = umlConnectorEngine.getConnector(id);
            if (connector) {
                return [
                    {
                        id,
                        type: 'connector',
                        kind: connector.kind,
                        label: labelForConnector(connector, (shapeId) => umlCanvasEngine.getShape(shapeId)),
                        depth: 0,
                        isTopLevel: true,
                    },
                ];
            }
            return [];
        });
    }

    render() {
        const items = this.getItems();

        return createPortal(
            <div {...this.panelProps} style={this.panelStyle} className={`jx-panel uhid-panel ${this.panelActiveClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="Hidden Items"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={TITLE_BAR_ACTIONS(this.handleUnhideAll, this.handleClose)}
                />
                <div className="uhid-list">
                    {items.length === 0 && <div className="uhid-empty">No hidden items</div>}
                    {items.map((item) => (
                        <UmlHiddenItemsRow key={item.id} item={item} onShow={this.handleShowItem} />
                    ))}
                </div>
                {this.renderResizeHandles()}
            </div>,
            document.body,
        );
    }
}
